// 今日やること: 判断待ち（回答できる）・実行中・最近完了の案件。
import { Link } from 'react-router';
import { RefreshCw } from 'lucide-react';
import { api, officePath } from '../lib/api';
import { CASE_KIND_LABEL, CASE_STATE_LABEL, formatDateTime, questionText, recent } from '../lib/domain';
import { useLoad } from '../lib/useLoad';
import type { Client, Items, WorkCase } from '../lib/types';
import { CaseAnswerForm } from '../components/CaseAnswer';
import { NeedOffice } from '../components/Shell';
import { Badge, Button, Card, ErrorText, Loading, Muted, PageTitle } from '../components/ui';

interface Board {
  waiting: WorkCase[];
  running: WorkCase[];
  done: WorkCase[];
  clients: Map<string, Client>;
}

async function loadBoard(officeId: string): Promise<Board> {
  const get = (state: string) => api<Items<WorkCase>>(`${officePath(officeId, 'cases')}?state=${state}`, { officeId }).then((r) => r.items);
  const [waiting, running, done, clients] = await Promise.all([
    get('waiting'),
    get('running'),
    get('done'),
    api<Items<Client>>(officePath(officeId, 'clients'), { officeId }).then((r) => r.items),
  ]);
  return { waiting: recent(waiting, 100), running: recent(running, 100), done: recent(done, 10), clients: new Map(clients.map((c) => [c.id, c])) };
}

function ClientName({ c, clients }: { c: WorkCase; clients: Map<string, Client> }) {
  if (!c.clientId) return <span className="text-ink-3">利用者なし</span>;
  const cl = clients.get(c.clientId);
  return (
    <Link to={`/clients/${encodeURIComponent(c.clientId)}`} className="font-medium text-accent hover:underline">
      {cl?.name ?? '（名前を取得できない利用者）'}
    </Link>
  );
}

function CaseHead({ c, clients }: { c: WorkCase; clients: Map<string, Client> }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <ClientName c={c} clients={clients} />
      <span className="text-ink-3">{CASE_KIND_LABEL[c.kind] ?? c.kind}</span>
      <Badge tone={c.state === 'waiting' ? 'alert' : c.state === 'done' ? 'confirmed' : 'neutral'}>{CASE_STATE_LABEL[c.state] ?? c.state}</Badge>
      <span className="ml-auto text-xs text-ink-4">{formatDateTime(c.updatedAt)}</span>
    </div>
  );
}

function Board({ officeId }: { officeId: string }) {
  const { data, error, loading, reload } = useLoad(() => loadBoard(officeId), [officeId]);
  return (
    <>
      <PageTitle
        actions={
          <Button variant="secondary" size="sm" onClick={reload} disabled={loading}>
            <RefreshCw size={14} />
            読み込み直す
          </Button>
        }
      >
        今日やること
      </PageTitle>
      <ErrorText>{error}</ErrorText>
      {loading && !data ? <Loading /> : null}
      {data ? (
        <div className="space-y-8">
          <section className="space-y-3">
            <h2 className="text-base font-semibold">
              判断待ち <span className="text-ink-3">{data.waiting.length}件</span>
            </h2>
            {data.waiting.length === 0 ? <Muted>判断を求められている案件はありません。</Muted> : null}
            {data.waiting.map((c) => (
              <Card key={`${c.id}:${c.generation}`} className="space-y-3" data-testid="case-waiting">
                <CaseHead c={c} clients={data.clients} />
                <CaseAnswerForm officeId={officeId} caseId={c.id} generation={c.generation} question={questionText(c)} />
              </Card>
            ))}
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold">
              実行中 <span className="text-ink-3">{data.running.length}件</span>
            </h2>
            {data.running.length === 0 ? <Muted>実行中の案件はありません。</Muted> : null}
            {data.running.map((c) => (
              <Card key={c.id}>
                <CaseHead c={c} clients={data.clients} />
              </Card>
            ))}
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold">最近完了</h2>
            {data.done.length === 0 ? <Muted>完了した案件はまだありません。</Muted> : null}
            {data.done.map((c) => (
              <Card key={c.id}>
                <CaseHead c={c} clients={data.clients} />
              </Card>
            ))}
          </section>
        </div>
      ) : null}
    </>
  );
}

export function DashboardPage() {
  return <NeedOffice>{(officeId) => <Board key={officeId} officeId={officeId} />}</NeedOffice>;
}
